import { db } from "./firebase";
import { doc, getDoc, setDoc, updateDoc, deleteDoc } from "firebase/firestore";

/**
 * Obtiene el documento completo de un usuario en Firestore
 * @param {string} uid - UID del usuario autenticado
 * @returns {Promise<Object|null>} - Datos del documento o null si no existe
 */
export async function getUserData(uid) {
  try {
    const docRef = doc(db, "Users", uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      console.warn("No existe documento para este UID:", uid);
      return null;
    }
  } catch (error) {
    console.error("Error al obtener datos del usuario:", error);
    throw error;
  }
}

/**
 * Obtiene las materias de un curso específico
 * @param {string} cursoId - ID del curso (ej. "EXUNI-BG", "COLBACH")
 * @returns {Promise<Array>} - Lista de materias
 */
export async function getCourseSubjects(cursoId) {
  try {
    const docRef = doc(db, "Cursos", cursoId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();

      // Transformar los campos en un array de materias
      const materias = Object.keys(data).map((key) => {
        return {
          codigo: key,
          contenido: data[key],
        };
      });

      return materias;
    } else {
      console.warn("No existe curso con ID:", cursoId);
      return [];
    }
  } catch (error) {
    console.error("Error al obtener materias:", error);
    throw error;
  }
}

export const updateUserData = async (uid, data) => {
  const userRef = doc(db, "Users", uid);
  await updateDoc(userRef, data);
};


/**
 * Crea un documento de usuario en Firestore
 * @param {string} uid - UID del usuario
 * @param {Object} data - Datos del usuario
 */
export async function createUser(uid, data) {
  try {
    await setDoc(doc(db, "Users", uid), data);
    console.log("✅ Usuario creado con UID:", uid);
  } catch (error) {
    console.error("Error al crear usuario:", error);
    throw error;
  }
}

/**
 * Actualiza un documento de usuario en Firestore
 * @param {string} uid - UID del usuario
 * @param {Object} data - Campos a actualizar
 */
export async function updateUser(uid, data) {
  try {
    await updateDoc(doc(db, "Users", uid), data);
    console.log("✅ Usuario actualizado:", uid);
  } catch (error) {
    console.error("Error al actualizar usuario:", error);
    throw error;
  }
}

/**
 * Elimina un documento de usuario en Firestore
 * @param {string} uid - UID del usuario
 */
export async function deleteUser(uid) {
  try {
    await deleteDoc(doc(db, "Users", uid));
    console.log("✅ Usuario eliminado:", uid);
  } catch (error) {
    console.error("Error al eliminar usuario:", error);
    throw error;
  }
}

export async function getUserDataCached(uid) {
  const cacheKey = `user_${uid}`;
  const cache = sessionStorage.getItem(cacheKey);

  if (cache) {
    // console.log("Se leyeron datos del usuario de caché");
    return JSON.parse(cache);
  }

  const docRef = doc(db, "Users", uid);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const data = docSnap.data();
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
    //console.log("Se leyeron datos del usuario de Firestore");
    return data;
  }

  return null;
}

export function updateUserCache(uid, nuevosDatos) {
  sessionStorage.setItem(`user_${uid}`, JSON.stringify(nuevosDatos));
}

export function actualizarCacheLocal(uid, temaId, puntaje) {
  const cacheKey = `user_${uid}`;
  const cache = sessionStorage.getItem(cacheKey);

  if (cache) {
    const datos = JSON.parse(cache);

    // Si no existe el map de calificaciones, lo inicializamos
    if (!datos["19 Calificaciones"]) {
      datos["19 Calificaciones"] = {};
    }

    // Actualizamos solo la clave correspondiente
    datos["19 Calificaciones"][temaId] = String(puntaje);

    // Guardamos nuevamente en caché
    sessionStorage.setItem(cacheKey, JSON.stringify(datos));
    console.log(`✅ Caché actualizada: ${temaId} = ${String(puntaje)}`);
  } else {
    console.warn("⚠️ No se encontró caché para el usuario, se recomienda refrescar desde Firestore.");
  }
}

export async function saveUserGrade(uid, temaId, puntaje) {
  try {
    const userRef = doc(db, "Users", uid);
    await updateDoc(userRef, {
      [`19 Calificaciones.${temaId}`]: String(puntaje)
    });
    //console.log(`✅ Calificación ${puntaje} guardada en ${temaId} para usuario ${uid}`);
  } catch (error) {
    console.error("❌ Error al guardar calificación:", error);
    throw error;
  }
}